import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from './IntroStep.module.scss'
import templateCards from './introTemplates.json'
import { onTemplateOpened } from '../../gaEvents'
import { BRAND_CONFIG } from '../../branding/brandConfig'
import {
  getTemplateIdFromUrl,
  setTemplateInUrl,
  clearUrlState,
  setUrlTabLoad,
  setLibraryLoad,
} from '../../services/urlState'
import SignupBanner from '../SignupBanner/SignupBanner'
import HomeDemo from './home/HomeDemo'
import TemplateRail from './home/TemplateRail'
import MappingPreview from './home/MappingPreview'
import ChartGlyph from './home/ChartGlyph'
import { getChartGlyphs } from './home/chartGlyphs'

const MAP_NOTES = [
  'Required slots are checked off as you fill them, so you always know what’s left.',
  'Anything not required stays collapsed until you reach for it.',
  'Every field shows its detected type, so you catch a bad column before you chart it.',
]

export default function IntroStep({ onStartBlank, onTemplateLoaded }) {
  const [demoLoading, setDemoLoading] = useState(false)

  // Labels and count come from the chart registry, so the page can never
  // advertise a chart the builder does not actually offer.
  const glyphs = useMemo(() => getChartGlyphs(), [])

  const handleLoadTemplate = useCallback(
    async (fileName) => {
      try {
        setDemoLoading(true)
        const response = await fetch(
          `${process.env.PUBLIC_URL}/intro-templates/${fileName}`
        )
        if (!response.ok) throw new Error(`Unable to load ${fileName}.`)
        await onTemplateLoaded(await response.text())
      } catch (error) {
        window.console.error(error)
      } finally {
        setDemoLoading(false)
      }
    },
    [onTemplateLoaded]
  )

  const openTemplate = useCallback(
    (template) => {
      setTemplateInUrl(template.id)
      onTemplateOpened(template.id, template.title)
      handleLoadTemplate(template.fileName)
    },
    [handleLoadTemplate]
  )

  // Auto-load template from URL on mount (e.g. ?template=viz-nba-game5)
  useEffect(() => {
    const id = getTemplateIdFromUrl()
    if (!id) return
    const match = templateCards.find((t) => t.id === id)
    if (!match) return
    onTemplateOpened(match.id, match.title)
    handleLoadTemplate(match.fileName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToCharts = () => {
    const el = document.getElementById('chart-types')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className={styles.home}>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>
          <span>Paste the mess.</span>
          <span>Ship the chart.</span>
        </h1>
        <p className={styles.heroLede}>
          Data, map, style, export. Four steps from a raw file to a publishable
          chart.
        </p>

        <div className={styles.heroActions}>
          <button
            type="button"
            className={styles.heroPrimary}
            onClick={() => {
              clearUrlState()
              onStartBlank()
            }}
          >
            Build a chart
          </button>
          <a href="#templates" className={styles.heroSecondary}>
            Explore examples →
          </a>
        </div>

        <p className={styles.heroAlt}>
          <button
            type="button"
            className={styles.heroAltLink}
            onClick={() => {
              setLibraryLoad()
              onStartBlank()
            }}
          >
            Sample library
          </button>
          <span aria-hidden="true"> · </span>
          <button
            type="button"
            className={styles.heroAltLink}
            onClick={() => {
              setUrlTabLoad()
              onStartBlank()
            }}
          >
            Load from a URL
          </button>
        </p>

        <p className={styles.heroSmallScreenNote}>
          Charting works best on a larger screen.
        </p>
      </section>

      <HomeDemo />

      <TemplateRail onOpenTemplate={openTemplate} loading={demoLoading} />

      {/* ── Privacy ──────────────────────────────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Your files never leave the tab.</h2>
        <p className={styles.sectionLede}>
          Parsing, typing and rendering all happen locally. Paste a file or drop
          one in and the rows stay on your machine — unreleased numbers are
          never uploaded.
        </p>
      </section>

      {/* ── Chart types ──────────────────────────────────────────────── */}
      <section id="chart-types" className={styles.section}>
        <h2 className={styles.sectionTitle}>
          The charts other tools make you code.
        </h2>
        <p className={styles.sectionLede}>
          Most builders stop at bar, line and pie, then hand you a D3 tutorial
          for anything harder. {BRAND_CONFIG.productName} maps the awkward ones
          from your columns exactly like the easy ones.
        </p>
        <p className={styles.sectionEyebrowCount}>
          All {glyphs.length} chart types
        </p>
        <ul className={styles.glyphGrid}>
          {glyphs.map((glyph) => (
            <li key={glyph.key} className={styles.glyphItem}>
              <span className={styles.glyphTile}>
                <ChartGlyph index={glyph.index} className={styles.glyphSvg} />
              </span>
              <span className={styles.glyphLabel}>{glyph.name}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Auto-map ─────────────────────────────────────────────────── */}
      <section className={styles.sectionSplit}>
        <div className={styles.sectionSplitText}>
          <h2 className={styles.sectionTitle}>Auto-map takes the first pass.</h2>
          <p className={styles.sectionLede}>
            It reads your column types and fills the chart’s slots for you —
            axis, size, colour, groups. Override any of them, and switching
            chart type keeps the mapping you already made.
          </p>
          <div className={styles.mapNotes}>
            {MAP_NOTES.map((note) => (
              <p key={note}>{note}</p>
            ))}
          </div>
        </div>
        <MappingPreview onSwitchChart={scrollToCharts} />
      </section>

      {/* ── Roadmap ──────────────────────────────────────────────────── */}
      <section className={styles.band}>
        <div className={styles.bandInner}>
          <p className={styles.bandEyebrow}>On the roadmap</p>
          <h2 className={styles.sectionTitle}>Describe it, get the chart.</h2>
          <p className={styles.sectionLede}>
            The Assistant already reads your columns and tells you which charts
            fit. Next it takes the sentence too — same mapping step, same
            editable output, nothing you can’t see or change.
          </p>
          <p className={styles.bandQuote}>
            “Split this by region and use our brand palette”
          </p>
        </div>
      </section>

      {/* ── Signup ───────────────────────────────────────────────────── */}
      <section className={styles.signupSection}>
        <h2 className={styles.signupTitle}>One note when something ships.</h2>
        <SignupBanner />
        <p className={styles.signupLinks}>
          <a
            href="https://lugsail.substack.com/p/why-i-built-lugsailapp"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why I’m building {BRAND_CONFIG.productName} ↗
          </a>
          <span aria-hidden="true"> · </span>
          <a href={`mailto:${BRAND_CONFIG.footer.contact.email}`}>
            {BRAND_CONFIG.footer.contact.email}
          </a>
        </p>
      </section>
    </div>
  )
}
